import * as React from 'react';
import mergeRefs from 'react-merge-refs';
import Portal, { PortalElement } from '../shared/portal';
import { PopoverWidthType, SelectPlacement } from './types';
import {
  FloatingOverlay,
  FloatingRootContext,
  autoUpdate,
  flip,
  offset,
  size,
  useFloating,
} from '@floating-ui/react';
import { useTheme } from '@veneer/theme';
import useStyles from './use_styles';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { POPOVER_WIDTH_AUTO, POPOVER_WIDTH_FIT } from './defaults';

const SelectPopover: React.FC<{
  visibleOptionsHeight?: number;
  children: React.FunctionComponentElement<unknown>;
  onBackdropClick: () => void;
  placement?: SelectPlacement;
  portalContainer?: PortalElement;
  zIndex: number;
  rootContext: FloatingRootContext;
  popoverRef: React.Ref<HTMLDivElement>;
  popoverWidth: PopoverWidthType;
}> = ({
  children,
  visibleOptionsHeight,
  onBackdropClick,
  placement = 'bottom',
  portalContainer,
  zIndex,
  rootContext,
  popoverRef,
  popoverWidth,
}) => {
  const theme = useTheme();
  const isHc = theme.mode === 'contrast';
  const isPill = theme.shape === 'pill';

  const handleBackdropClick = React.useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(
    (event) => {
      if (event.currentTarget !== event.target) return;

      onBackdropClick?.();
    },
    [onBackdropClick]
  );

  const style = useStyles();

  const finalPlacement: 'bottom-start' | 'top-start' = placement.startsWith(
    'bottom'
  )
    ? 'bottom-start'
    : 'top-start';

  const { floatingStyles, refs } = useFloating({
    placement: finalPlacement,
    middleware: [
      flip(),
      offset(parseInt(theme.orbitalTwo)),
      size({
        apply({ rects, elements, availableWidth, availableHeight }) {
          Object.assign(elements.floating.style, {
            ...(visibleOptionsHeight && {
              height: `${visibleOptionsHeight}px`,
            }),
            width: `${
              popoverWidth === POPOVER_WIDTH_AUTO
                ? rects.reference.width + 'px'
                : popoverWidth === POPOVER_WIDTH_FIT
                  ? 'auto'
                  : popoverWidth + 'px'
            }`,
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    rootContext,
    whileElementsMounted: autoUpdate,
  });

  return (
    <Portal container={portalContainer}>
      <FloatingOverlay
        data-popover-backdrop
        aria-hidden
        onClick={handleBackdropClick}
        style={{ zIndex }}
      />
      <div
        ref={mergeRefs([popoverRef, refs.setFloating])}
        style={floatingStyles}
        css={[
          { zIndex },
          style.content.base,
          isHc && style.content.hc,
          isPill && style.content.pill,
          highContrastMediaQuery(theme),
        ]}
      >
        {children}
      </div>
    </Portal>
  );
};

SelectPopover.displayName = 'SelectPopover';

export default SelectPopover;
